// Add this at the top for TypeScript global extension
declare global {
    interface Window {
      ThriveStackPricingCalculator?: any;
      APP_PRICING_PAGE?: string;
    }
  }
  
  import React, { useEffect, useRef, useCallback, useState } from "react";
  
  const SCRIPT_ID = "thrive-pricing-script";
  const CONTAINER_ID = "thrivestack-pricing-page";
  
  interface PricingPlanProps {
    scriptUrl?: string;
  }
  
  const PricingPlan: React.FC<PricingPlanProps> = ({ scriptUrl }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scriptError, setScriptError] = useState(false);
    // Use env, then prop, then window, then fallback
    const SCRIPT_SRC = `${process.env.APP_PRICING_PAGE}`
    useEffect(() => {
      // Store original styles to restore later
      const originalBodyStyle = {
        overflow: document.body.style.overflow,
        height: document.body.style.height,
        maxHeight: document.body.style.maxHeight
      };
      
      const originalHtmlStyle = {
        overflow: document.documentElement.style.overflow,
        height: document.documentElement.style.height,
        maxHeight: document.documentElement.style.maxHeight
      };
  
      // Force scrollable styles on body and html
      const forceScrollableStyles = () => {
        document.body.style.overflow = 'auto';
        document.body.style.height = 'auto';
        document.body.style.maxHeight = 'none';
        
        document.documentElement.style.overflow = 'auto';
        document.documentElement.style.height = 'auto';
        document.documentElement.style.maxHeight = 'none';
      };
  
      // Apply immediately
      forceScrollableStyles();
  
      // Remove previous widget content for reinitialization
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
  
      // Script loader with reinit support
      const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
      
      const initializeWidget = () => {
        if (window.ThriveStackPricingCalculator) {
          new window.ThriveStackPricingCalculator(CONTAINER_ID);
          // Call fixScrolling once immediately
          const container = document.getElementById(CONTAINER_ID);
          if (container) {
            // Reset container styles
            container.style.height = 'auto';
            container.style.minHeight = 'auto';
            container.style.maxHeight = 'none';
            container.style.overflow = 'visible';
            container.style.position = 'static';
            // Only select elements inside the container
            const allElements = container.querySelectorAll('*');
            allElements.forEach((element: Element) => {
              const el = element as HTMLElement;
              const computedStyle = window.getComputedStyle(el);
              if (
                computedStyle.height === '100vh' || 
                computedStyle.height === '100%' ||
                computedStyle.maxHeight === '100vh' ||
                computedStyle.maxHeight === '100%' ||
                computedStyle.overflow === 'hidden'
              ) {
                el.style.height = 'auto';
                el.style.minHeight = 'auto';
                el.style.maxHeight = 'none';
                el.style.overflow = 'visible';
              }
              if (computedStyle.position === 'fixed' && el.id === CONTAINER_ID) {
                el.style.position = 'static';
              }
            });
          }
          // MutationObserver logic remains unchanged
          const observer = new MutationObserver(() => {
            forceScrollableStyles();
          });
          observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['style'],
            subtree: true
          });
          (containerRef.current as any)._observer = observer;
        }
      };
  
      if (!existingScript) {
        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.onload = initializeWidget;
        script.onerror = () => setScriptError(true);
        document.body.appendChild(script);
      } else {
        initializeWidget();
      }
  
      // Cleanup function
      return () => {
        // Clean up mutation observer
        if ((containerRef.current as any)?._observer) {
          (containerRef.current as any)._observer.disconnect();
        }
        
        // Optionally restore original styles if needed
        // document.body.style.overflow = originalBodyStyle.overflow;
        // document.body.style.height = originalBodyStyle.height;
        // document.body.style.maxHeight = originalBodyStyle.maxHeight;
        
        // document.documentElement.style.overflow = originalHtmlStyle.overflow;
        // document.documentElement.style.height = originalHtmlStyle.height;
        // document.documentElement.style.maxHeight = originalHtmlStyle.maxHeight;
      };
    }, [SCRIPT_SRC]);
  
    if (scriptError) {
      return (
        <div style={{ padding: 32, textAlign: 'center', color: '#b91c1c', background: '#fef2f2', borderRadius: 12, margin: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 12 }}>Failed to load pricing widget.</div>
          <div style={{ marginBottom: 16 }}>Please check your internet connection and <b>refresh the page</b>.</div>
          <button
            style={{
              background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 600, cursor: 'pointer', fontSize: 16
            }}
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      );
    }
  
    return (
      <div
        id={CONTAINER_ID}
        ref={containerRef}
        style={{ 
          width: "100%",
          height: "auto",
          minHeight: "100vh",
          overflow: "visible",
          position: "static",
          display: "block"
        }}
      />
    );
  };
  
  export default PricingPlan;